import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { normalizeLang } from "@/lib/i18n";
import { HelpButton, getHelpCopy } from "@/components/HelpAccess";
import {
  activeInterviewSessionIdFromRoute,
  clearRememberedActiveInterviewRoute,
  getRememberedActiveInterviewSessionId,
  isActiveInterviewRoute,
  rememberActiveInterviewRoute,
} from "@/lib/activeInterviewNavigation";
import { confirmSignOutDuringInterview } from "@/lib/savedInterviewLifecycle";

/*
 * Compact user menu shown in the header of authenticated pages.
 * Displays the user's first name (or email) + a dropdown with account actions.
 */

export const DOWNLOAD_LABEL = {
  en: "My Reports & Downloads",
  "pt-BR": "Meus Relatórios e Downloads",
  "es-419": "Mis Informes y Descargas",
  "fr-CA": "Mes rapports et téléchargements",
};

export const ACCOUNT_LABEL = {
  en: "Account settings",
  "pt-BR": "Configurações da conta",
  "es-419": "Configuración de la cuenta",
  "fr-CA": "Paramètres du compte",
};

export const MENU_LABEL = {
  en: "Account menu",
  "pt-BR": "Menu da conta",
  "es-419": "Menú de la cuenta",
  "fr-CA": "Menu du compte",
};

export const SIGN_OUT_LABEL = {
  en: "Sign out",
  "pt-BR": "Sair",
  "es-419": "Cerrar sesión",
  "fr-CA": "Se déconnecter",
};

export function UserMenu({ showHelp = true, buttonClassName = "" }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger || typeof window === "undefined") return;
    const rect = trigger.getBoundingClientRect();
    const width = 240;
    const gutter = 8;
    const left = Math.max(gutter, Math.min(window.innerWidth - width - gutter, rect.right - width));
    setMenuPosition({ top: rect.bottom + 8, left });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onViewportChange = () => updateMenuPosition();
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);
    return () => {
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
    };
  }, [open, updateMenuPosition]);

  useEffect(() => {
    const onClick = (e) => {
      const insideTrigger = triggerRef.current?.contains(e.target);
      const insideDropdown = dropdownRef.current?.contains(e.target);
      if (!insideTrigger && !insideDropdown) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;
  const label = user.first_name || user.email;
  const lang = normalizeLang(user.preferred_language);
  const helpCopy = getHelpCopy(lang);
  const closeMenu = () => setOpen(false);
  const leaveInterviewTemporarily = () => {
    if (isActiveInterviewRoute(location.pathname)) {
      rememberActiveInterviewRoute(location.pathname, location.search);
    }
    closeMenu();
  };

  const signOut = async () => {
    closeMenu();

    // Explicit sign-out is an account boundary, not a temporary interview
    // detour. Capture the active session before clearing local resume state so
    // we can best-effort abandon unfinished working data for the outgoing user.
    const activeSessionId = isActiveInterviewRoute(location.pathname)
      ? activeInterviewSessionIdFromRoute(location.pathname)
      : getRememberedActiveInterviewSessionId();

    // During an interview, ask first (2026-11-01): Save & Exit (it signs out and
    // leaves by itself), exit without saving, or cancel and stay.
    if (activeSessionId) {
      const choice = await confirmSignOutDuringInterview(activeSessionId, lang);
      if (choice !== "exit") return;
    }

    clearRememberedActiveInterviewRoute();

    // Move to a clean login route BEFORE auth state is cleared. Otherwise the
    // protected interview route redirects to /login with location.state.from,
    // and a different account can be sent back to the previous user's session.
    navigate("/login", { replace: true, state: null });

    if (activeSessionId) {
      try {
        await api.post(`/sessions/${encodeURIComponent(activeSessionId)}/abandon`);
      } catch {
        // Sign-out must still succeed. Server-side lease/TTL cleanup remains
        // the fallback if the best-effort abandon request cannot be delivered.
      }
    }

    await logout();
  };

  const dropdown = open && typeof document !== "undefined"
    ? createPortal(
        <nav
          ref={dropdownRef}
          id="user-account-navigation"
          aria-label={MENU_LABEL[lang] || MENU_LABEL.en}
          className="fixed w-60 rounded-xl border border-[color:var(--border-stone)] bg-white shadow-xl py-1 z-[1000] normal-case tracking-normal"
          style={{ top: menuPosition.top, left: menuPosition.left }}
          data-testid="user-menu-dropdown"
        >
          <div className="px-3 py-2 text-[11px] text-[color:var(--text-secondary)] break-all">
            {user.email}
          </div>
          <div className="h-px bg-[color:var(--border-stone)]" />
          <Link
            to="/account"
            onClick={leaveInterviewTemporarily}
            className="block px-3 py-2 text-xs text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)] font-medium"
            data-testid="user-menu-account"
          >
            {ACCOUNT_LABEL[lang] || ACCOUNT_LABEL.en}
          </Link>
          <Link
            to="/downloads"
            onClick={leaveInterviewTemporarily}
            className="block px-3 py-2 text-xs text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)] font-medium"
            data-testid="user-menu-downloads"
          >
            {DOWNLOAD_LABEL[lang] || DOWNLOAD_LABEL.en}
          </Link>
          <button
            type="button"
            onClick={closeMenu}
            className="w-full text-left px-3 py-2 text-xs text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)] font-medium"
            data-testid="user-menu-help"
          >
            {helpCopy.menu}
          </button>
          {user.is_admin && (
            <>
              <div className="h-px bg-[color:var(--border-stone)]" />
              <Link
                to="/admin"
                onClick={leaveInterviewTemporarily}
                className="block px-3 py-2 text-xs text-[color:var(--brand)] hover:bg-[color:var(--bg-surface-hover)] font-medium"
                data-testid="user-menu-admin"
              >
                Admin dashboard {/* l10n-ignore: admin only */}
              </Link>
              <Link
                to="/admin/beta-codes"
                onClick={leaveInterviewTemporarily}
                className="block px-3 py-2 text-xs text-[color:var(--brand)] hover:bg-[color:var(--bg-surface-hover)]"
                data-testid="user-menu-admin-beta"
              >
                Beta codes admin {/* l10n-ignore: admin only */}
              </Link>
              <Link
                to="/admin/users"
                onClick={leaveInterviewTemporarily}
                className="block px-3 py-2 text-xs text-[color:var(--brand)] hover:bg-[color:var(--bg-surface-hover)]"
                data-testid="user-menu-admin-users"
              >
                Users admin {/* l10n-ignore: admin only */}
              </Link>
              <Link
                to="/admin/audit"
                onClick={leaveInterviewTemporarily}
                className="block px-3 py-2 text-xs text-[color:var(--brand)] hover:bg-[color:var(--bg-surface-hover)]"
                data-testid="user-menu-admin-audit"
              >
                Audit log {/* l10n-ignore: admin only */}
              </Link>
            </>
          )}
          <div className="h-px bg-[color:var(--border-stone)]" />
          <button
            type="button"
            onClick={() => void signOut()}
            className="w-full text-left px-3 py-2 text-xs text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)]"
            data-testid="user-menu-signout"
          >
            {SIGN_OUT_LABEL[lang] || SIGN_OUT_LABEL.en}
          </button>
        </nav>,
        document.body,
      )
    : null;

  return (
    <div className="flex min-w-0 items-center gap-3">
      {showHelp && <HelpButton />}
      <div className="relative min-w-0" ref={triggerRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`inline-flex min-w-0 max-w-[6.25rem] min-[420px]:max-w-[9rem] sm:max-w-none shrink-0 items-center gap-2 overflow-hidden rounded-full border border-[color:var(--border-stone)] bg-white px-3 py-1.5 text-[11px] font-medium tracking-normal normal-case text-[color:var(--text-primary)] hover:bg-[color:var(--bg-surface-hover)] ${buttonClassName}`}
          data-testid="user-menu-button"
          aria-expanded={open}
          aria-haspopup="true"
          aria-controls="user-account-navigation"
          title={label}
        >
          <span className="min-w-0 max-w-[3.25rem] min-[420px]:max-w-[6rem] sm:max-w-[13rem] truncate">{label}</span>
          <span
            aria-hidden="true"
            className={`shrink-0 text-[8px] leading-none text-[color:var(--text-secondary)] transition-transform ${open ? "rotate-180" : ""}`}
          >
            ▼
          </span>
        </button>
      </div>
      {dropdown}
    </div>
  );
}